import React, { useEffect, useState } from 'react';
import { apiService } from '@/services/api';
import type { NewOrderItemInput, Order, OrderBranch } from '@/types/order';
import styles from './CreateOrderModal.module.scss';

interface CreateOrderModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-select a branch (e.g. opened from a branch page). */
  defaultBranchId?: string;
  /** Called after the order is created (and optionally submitted). */
  onCreated: (order: Order) => void;
}

interface ItemRow extends NewOrderItemInput {
  key: string;
}

const newRow = (i: number): ItemRow => ({ key: `row-${i}-${Math.round(performance.now())}`, name: '', quantity: 1, unit: '' });

const CreateOrderModal: React.FC<CreateOrderModalProps> = ({ open, onClose, defaultBranchId, onCreated }) => {
  const [branches, setBranches] = useState<OrderBranch[]>([]);
  const [branchId, setBranchId] = useState<string>(defaultBranchId ?? '');
  const [note, setNote] = useState('');
  const [rows, setRows] = useState<ItemRow[]>([newRow(0)]);
  const [submitting, setSubmitting] = useState(false);
  const [submitToBranch, setSubmitToBranch] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loadingBranches, setLoadingBranches] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingBranches(true);
    apiService
      .getOrderBranches()
      .then((list) => {
        setBranches(list);
        if (!defaultBranchId && list.length === 1) setBranchId(list[0].id);
      })
      .catch(() => setError('Could not load branches.'))
      .finally(() => setLoadingBranches(false));
  }, [open, defaultBranchId]);

  useEffect(() => {
    if (open && defaultBranchId) setBranchId(defaultBranchId);
  }, [open, defaultBranchId]);

  if (!open) return null;

  const updateRow = (key: string, patch: Partial<ItemRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };
  const addRow = () => setRows((prev) => [...prev, newRow(prev.length)]);
  const removeRow = (key: string) =>
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev));

  const validate = (): NewOrderItemInput[] | null => {
    const cleaned = rows
      .map((r) => ({ name: r.name.trim(), quantity: Number(r.quantity), unit: r.unit?.trim() || undefined }))
      .filter((r) => r.name.length > 0);
    if (!branchId) {
      setError('Please choose a branch.');
      return null;
    }
    if (cleaned.length === 0) {
      setError('Add at least one item.');
      return null;
    }
    if (cleaned.some((r) => !Number.isFinite(r.quantity) || r.quantity < 1)) {
      setError('Every item needs a quantity of 1 or more.');
      return null;
    }
    return cleaned;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    const items = validate();
    if (!items) return;

    setSubmitting(true);
    try {
      let order = await apiService.createOrder({ branchId, note: note.trim() || undefined, items });
      if (submitToBranch) {
        try {
          order = await apiService.submitOrder(order.id);
        } catch (subErr: any) {
          // Order exists as a draft even if submit failed — surface but still return it.
          setError(subErr?.message || 'Order created as draft, but submitting to the branch failed.');
        }
      }
      onCreated(order);
      // Reset for next time.
      setRows([newRow(0)]);
      setNote('');
      if (!defaultBranchId) setBranchId('');
    } catch (err: any) {
      const fe = err?.fieldErrors as Record<string, string> | undefined;
      if (fe) setFieldErrors(fe);
      setError(err?.message || 'Could not create the order.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="Create order" onMouseDown={onClose}>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>New order</h2>
          <button type="button" className={styles.closeBtn} aria-label="Close" onClick={onClose}>×</button>
        </div>

        <p className={styles.hint}>
          Add the items you need. The order number is assigned automatically once you create it.
        </p>

        <form onSubmit={handleSubmit}>
          <label className={styles.label} htmlFor="order-branch">Branch</label>
          <select
            id="order-branch"
            className={styles.select}
            value={branchId}
            disabled={!!defaultBranchId || loadingBranches}
            onChange={(e) => setBranchId(e.target.value)}
          >
            <option value="">{loadingBranches ? 'Loading branches…' : 'Select a branch…'}</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>

          <div className={styles.itemsHeader}>
            <span className={styles.label}>Items</span>
            <button type="button" className={styles.addBtn} onClick={addRow}>+ Add item</button>
          </div>

          <div className={styles.rows}>
            {rows.map((r) => (
              <div className={styles.row} key={r.key}>
                <input
                  className={styles.itemName}
                  placeholder="Item name"
                  value={r.name}
                  onChange={(e) => updateRow(r.key, { name: e.target.value })}
                />
                <input
                  className={styles.qty}
                  type="number"
                  min={1}
                  step={1}
                  aria-label="Quantity"
                  value={r.quantity}
                  onChange={(e) => updateRow(r.key, { quantity: e.target.value === '' ? 1 : Number(e.target.value) })}
                />
                <input
                  className={styles.unit}
                  placeholder="Unit"
                  value={r.unit ?? ''}
                  onChange={(e) => updateRow(r.key, { unit: e.target.value })}
                />
                <button
                  type="button"
                  className={styles.removeBtn}
                  aria-label="Remove item"
                  onClick={() => removeRow(r.key)}
                  disabled={rows.length === 1}
                >×</button>
              </div>
            ))}
          </div>

          <label className={styles.label} htmlFor="order-note">Note (optional)</label>
          <textarea
            id="order-note"
            className={styles.note}
            rows={2}
            placeholder="Anything the branch should know…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />

          <label className={styles.checkbox}>
            <input type="checkbox" checked={submitToBranch} onChange={(e) => setSubmitToBranch(e.target.checked)} />
            <span>Submit to branch immediately</span>
          </label>

          {Object.keys(fieldErrors).length > 0 && (
            <ul className={styles.fieldErrors}>
              {Object.entries(fieldErrors).map(([f, m]) => (
                <li key={f}>{f}: {m}</li>
              ))}
            </ul>
          )}
          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.actions}>
            <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className={styles.primaryBtn} disabled={submitting}>
              {submitting ? 'Creating…' : submitToBranch ? 'Create & submit' : 'Create draft'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOrderModal;
